/**
 * MySubmissionsTab — the panel's "My Submissions" tab content.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3 AC3.
 *
 * Re-homes StakeholderPortalPage's "Your Issues" surface into the header
 * panel, reorganized per AC3: submissions with an UNSEEN update render BEFORE
 * seen ones, and within each group by `last_updated` DESCENDING (own_only —
 * `is_unseen` comes from the reporter-scoped `/issues` read, R3's backend
 * fix). Every prior adversarial-review hardening finding is preserved
 * (AC8): dash-free client copy, the "You confirmed" state + its durable
 * undo, the two empty states (zero-rows / all-untriaged-received), and the
 * Lucide status icons (via StatusPill/statusMap) — this file lifts that
 * logic rather than rewriting it. Reuses the SAME cells the retired page
 * used: `data-table.tsx`, `StatusPill`, `StatusTimeline`, `deriveClientRung`,
 * `lastUpdatedLabel` (AC9 — no parallel status/roadmap mechanism).
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ImagePlus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { APP_VERSION } from '@/lib/version/versionCheck';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { clientPillLabel, deriveClientRung } from '@/lib/portal/clientRung';
import { getStatusConfig } from '@/lib/portal/statusMap';
import { StatusPill } from '@/components/portal/StatusPill';
import { StatusTimeline } from '@/components/portal/StatusTimeline';
import { SignedScreenshot } from '@/components/feedback/SignedScreenshot';
import { ImageGallery } from '@/components/feedback/ImageGallery';
import { lastUpdatedLabel, relativePast } from '@/lib/portal/lastUpdated';
import { portalWriteError } from '@/lib/portal/mutationError';
import { PanelErrorState } from '@/components/portal/panel/PanelErrorState';
import type { FeedbackItem } from '@/lib/schemas';

export const PANEL_SUBMISSIONS_QUERY_KEY = ['stakeholder-portal', 'panel', 'issues'] as const;

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const LADDER_BLURB: Record<string, string> = {
  received: "we're reviewing this",
  in_progress: "we're working on it",
  building_testing: "we've built the fix and are testing it",
  shipped_awaiting: "it's live. Can you confirm it's fixed?",
  // v0.17.15: she said "still broken" after the fix shipped. No Confirm /
  // Still broken buttons here (rowAction returns null for this key).
  still_broken_reported: "you told us it's still broken. We're looking at it.",
  verified: 'confirmed fixed',
  regressed: "it came back, so we're on it again",
  deferred: 'paused for now',
  wont_do: "we've decided not to take this on",
  superseded: "we're already tracking this",
  // v0.17.16: dismissed from triage.
  closed: "we've closed this report. Send a new one if it comes up again.",
};

const NOTE_REQUIRED_KEYS = new Set(['deferred', 'wont_do', 'regressed']);

const CONTINUITY_COPY: Record<string, string> = {
  superseded: "We're tracking this together with a related report. Updates still land here.",
  wont_do: "We've decided not to build this. See why below.",
  deferred: "Not something we're working on right now.",
};

const ALL_RECEIVED_COPY =
  "We've got your reports. Nothing's triaged yet. Updates will land right here.";

function clientBlurb(item: FeedbackItem): string | null {
  const key = deriveClientRung(item).key;
  return LADDER_BLURB[key] ?? null;
}

type RowAction = 'confirm' | 'still_broken_only' | 'undo' | null;

function rowAction(item: FeedbackItem): RowAction {
  const key = deriveClientRung(item).key;
  if (key === 'shipped_awaiting') return 'confirm';
  if (key === 'verified') return 'still_broken_only';
  if (key === 'you_confirmed') return 'undo';
  return null;
}

const WITHDRAWABLE_RUNGS = new Set(['received', 'in_progress', 'building_testing']);

function canWithdraw(item: FeedbackItem): boolean {
  return WITHDRAWABLE_RUNGS.has(deriveClientRung(item).key);
}

function isWithdrawn(item: FeedbackItem): boolean {
  return deriveClientRung(item).key === 'withdrawn';
}

const NOTE_TERMINAL_RUNGS = new Set(['you_confirmed', 'withdrawn', 'closed']);

function canAddNote(item: FeedbackItem): boolean {
  return !NOTE_TERMINAL_RUNGS.has(deriveClientRung(item).key);
}

function hasDetails(item: FeedbackItem): boolean {
  const key = item.claim_ladder.key;
  if (item.resolution_note) return true;
  if (NOTE_REQUIRED_KEYS.has(key)) return true;
  if (key === 'superseded') return true;
  if ((key === 'shipped_awaiting' || key === 'verified') && buildComparison(item)) return true;
  return false;
}

function buildComparison(item: FeedbackItem): string | null {
  if (!item.addressed_in_build) return null;
  if (item.addressed_in_build === APP_VERSION) {
    return `This was fixed in the version you're running (${item.addressed_in_build}).`;
  }
  return `Refresh to get the version with this fix (shipped in ${item.addressed_in_build}).`;
}

interface MySubmissionsTabProps {
  canResolve: boolean;
  /**
   * Layout density (visual-review finding, 2026-07-24 final pass): the narrow
   * 384px client Popover cannot host the full multi-column DataTable (the
   * status-blurb cell inherits whitespace-nowrap and forces a horizontally-
   * scrolling table into a notification-style popover). `'popover'` renders a
   * stacked card list instead; `'drawer'` (default — the wider super-admin
   * Sheet, and the historical/tested behavior for any caller that predates
   * this prop) keeps the dense DataTable.
   */
  variant?: 'popover' | 'drawer';
}

export function MySubmissionsTab({ canResolve, variant = 'drawer' }: MySubmissionsTabProps) {
  const queryClient = useQueryClient();
  const [stillBrokenFor, setStillBrokenFor] = useState<FeedbackItem | null>(null);
  const [note, setNote] = useState('');
  const [stillBrokenImage, setStillBrokenImage] = useState<File | null>(null);
  const [stillBrokenImageError, setStillBrokenImageError] = useState<string | null>(null);
  const stillBrokenFileRef = useRef<HTMLInputElement | null>(null);
  const [addNoteFor, setAddNoteFor] = useState<FeedbackItem | null>(null);
  const [noteText, setNoteText] = useState('');
  // R4 (SPEC-feedback-return-path-2026-08-17): answering an operator's question.
  const [answerFor, setAnswerFor] = useState<FeedbackItem | null>(null);
  const [answerText, setAnswerText] = useState('');

  const handleStillBrokenFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setStillBrokenImage(null);
      setStillBrokenImageError(null);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setStillBrokenImage(null);
      setStillBrokenImageError('Please choose a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setStillBrokenImage(null);
      setStillBrokenImageError(`That image is too large. Maximum size is ${MAX_IMAGE_SIZE_MB} MB.`);
      return;
    }
    setStillBrokenImage(file);
    setStillBrokenImageError(null);
  };

  const clearStillBrokenImage = () => {
    setStillBrokenImage(null);
    setStillBrokenImageError(null);
    if (stillBrokenFileRef.current) stillBrokenFileRef.current.value = '';
  };

  const closeStillBroken = () => {
    setStillBrokenFor(null);
    setNote('');
    clearStillBrokenImage();
  };

  const issuesQuery = useQuery({
    queryKey: PANEL_SUBMISSIONS_QUERY_KEY,
    queryFn: () => api.stakeholderPortal.issues(),
  });

  const unAcceptMutation = useMutation({
    mutationFn: (feedbackId: string) => api.stakeholderPortal.unAccept(feedbackId),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      if (data?.refused) {
        toast.error("Our team has finalised this one. If it's still broken, send a new report.");
      } else if (data?.reverted) {
        toast.success("Undone. We've reopened this.");
      } else {
        toast.success('Nothing to undo.');
      }
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not undo')),
  });

  const acceptMutation = useMutation({
    mutationFn: (feedbackId: string) => api.stakeholderPortal.accept(feedbackId),
    onSuccess: async (_data, feedbackId) => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success('Thanks for confirming. Marked resolved.', {
        duration: 8000,
        action: { label: 'Undo', onClick: () => unAcceptMutation.mutate(feedbackId) },
      });
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not mark resolved')),
  });

  const stillBrokenMutation = useMutation({
    mutationFn: (vars: { feedbackId: string; note: string; image: File | null }) =>
      api.stakeholderPortal.stillBroken(vars.feedbackId, vars.note, vars.image),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success("Thanks, we've reopened this and we're on it.");
      closeStillBroken();
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not report still-broken')),
  });

  const unWithdrawMutation = useMutation({
    mutationFn: (feedbackId: string) => api.stakeholderPortal.unWithdraw(feedbackId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success("Reopened. We're back on it.");
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not reopen')),
  });

  const withdrawMutation = useMutation({
    mutationFn: (feedbackId: string) => api.stakeholderPortal.withdraw(feedbackId),
    onSuccess: async (_data, feedbackId) => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success('Marked as no longer needed on your end. Changed your mind? Reopen it.', {
        duration: 8000,
        action: { label: 'Reopen', onClick: () => unWithdrawMutation.mutate(feedbackId) },
      });
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not withdraw')),
  });

  const addNoteMutation = useMutation({
    mutationFn: (vars: { feedbackId: string; note: string }) =>
      api.stakeholderPortal.addNote(vars.feedbackId, vars.note),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success("Thanks, we've added your note.");
      setAddNoteFor(null);
      setNoteText('');
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not add your note')),
  });

  const answerMutation = useMutation({
    mutationFn: (vars: { feedbackId: string; answer: string }) =>
      api.stakeholderPortal.answerQuestion(vars.feedbackId, vars.answer),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PANEL_SUBMISSIONS_QUERY_KEY });
      toast.success('Thanks, your answer was sent.');
      setAnswerFor(null);
      setAnswerText('');
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not send your answer')),
  });

  const items: FeedbackItem[] = issuesQuery.data?.feedback ?? [];

  // R3 AC3: unseen-before-seen, then last_updated DESC within each group. A
  // missing last_updated (untriaged/no activity) sorts as "oldest" within its
  // group (never crashes the comparator on a null timestamp).
  const orderedItems = useMemo(() => {
    const ts = (item: FeedbackItem) => (item.last_updated ? Date.parse(item.last_updated) : 0);
    return [...items].sort((a, b) => {
      const unseenDelta = Number(!!b.is_unseen) - Number(!!a.is_unseen);
      if (unseenDelta !== 0) return unseenDelta;
      return ts(b) - ts(a);
    });
  }, [items]);

  const anyDetails = items.some(hasDetails);
  const anyActionable = canResolve && items.some((item) => rowAction(item) !== null);
  const anyAnswerable = items.some((item) => !!item.operator_question && !item.reporter_answer);
  const anyClientAction =
    items.some((item) => canWithdraw(item) || isWithdrawn(item) || canAddNote(item)) || anyAnswerable;
  const allReceived = items.length > 0 && items.every((item) => !item.work_item_id);

  // Shared render blocks — used by BOTH the drawer's DataTable columns and
  // the popover's card list, so the two variants stay behaviorally identical
  // (same status pill / blurb / timeline / images / notes / actions), only
  // the surrounding layout differs.
  const renderStatusBlock = (item: FeedbackItem) => {
    const rung = deriveClientRung(item);
    const shortLabel = clientPillLabel(rung.key) ?? getStatusConfig(rung.key, rung.label).label;
    const blurb = clientBlurb(item);
    const updated = lastUpdatedLabel(item.last_updated ?? null);
    return (
      <div className="space-y-0.5" data-testid="portal-issue-status-cell">
        <div className="flex items-center gap-1.5">
          <StatusPill rung={rung} label={clientPillLabel(rung.key)} testId="portal-issue-status" />
          {item.is_unseen && (
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              data-testid="portal-issue-unseen-dot"
              aria-label="Unseen update"
            />
          )}
        </div>
        {rung.label !== shortLabel && (
          <span
            className="block text-xs font-medium text-foreground"
            data-testid="portal-issue-status-detail"
          >
            {rung.label}
          </span>
        )}
        {blurb && (
          <span className="block text-xs text-muted-foreground" data-testid="portal-issue-blurb">
            {blurb}
          </span>
        )}
        <StatusTimeline timeline={item.timeline ?? []} />
        {updated && (
          <span className="block text-[11px] text-muted-foreground" data-testid="portal-issue-last-updated">
            {updated}
          </span>
        )}
      </div>
    );
  };

  // `compact`: the popover card list caps the thumbnail row (fixed small
  // squares + a "+N more" affordance) instead of the drawer's uncapped,
  // variable-width strip — narrow-column visual-review finding, 2026-07-24.
  const renderReportBlock = (item: FeedbackItem, compact: boolean) => (
    <div className="space-y-2">
      <span className="text-sm whitespace-pre-wrap">{item.body}</span>
      {/* R5 shipped up to 5 attached images (`images[]`); this renders
          ALL of them, not just the single legacy screenshot_url — the
          cross-ring gap this closes. `legacyUrl` is a pure back-compat
          fallback for pre-R5 reports (images[] empty). */}
      <ImageGallery
        images={item.images}
        legacyUrl={item.screenshot_url}
        alt="Your attached screenshot"
        testId="portal-issue-screenshot"
        {...(compact
          ? { maxVisible: 3, imgClassName: 'h-12 w-12 rounded border object-cover' }
          : {})}
      />
      {item.regression_screenshot_url && (
        <div className="space-y-1" data-testid="portal-issue-regression-screenshot">
          <span className="block text-xs text-muted-foreground">Still-broken screenshot</span>
          <SignedScreenshot
            url={item.regression_screenshot_url}
            alt="Your still-broken screenshot"
          />
        </div>
      )}
      <IssueNotes item={item} />
      {/* R4 (SPEC-feedback-return-path-2026-08-17): a question from us, and
          your answer once you've sent one. Renders on an UNTRIAGED report
          too -- a question never requires the report to be triaged first. */}
      {item.operator_question && (
        <div className="space-y-1 rounded-md border bg-muted/40 p-2" data-testid="portal-question-answer">
          <p className="text-xs">
            <span className="font-medium">A question from us: </span>
            <span data-testid="portal-question-text">{item.operator_question}</span>
          </p>
          {item.reporter_answer ? (
            <p className="text-xs text-muted-foreground" data-testid="portal-answer-text">
              <span className="font-medium">Your answer: </span>
              {item.reporter_answer}
            </p>
          ) : (
            <p className="text-xs italic text-muted-foreground" data-testid="portal-answer-pending">
              You haven't answered yet.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderActionsRow = (item: FeedbackItem) => {
    const action = canResolve ? rowAction(item) : null;
    const showWithdraw = canWithdraw(item);
    const showReopen = isWithdrawn(item);
    const showAddNote = canAddNote(item);
    // R4: answerable whenever a question has been asked and not yet answered
    // -- regardless of triage state (a question never requires triage first).
    const showAnswer = !!item.operator_question && !item.reporter_answer;
    if (action === null && !showWithdraw && !showReopen && !showAddNote && !showAnswer) return null;
    return (
      <div className="flex flex-wrap justify-end gap-2" data-testid="portal-issue-actions">
        {showAnswer && (
          <Button
            size="sm"
            variant="outline"
            data-testid="portal-issue-answer"
            onClick={() => {
              setAnswerFor(item);
              setAnswerText('');
            }}
          >
            Answer
          </Button>
        )}
        {action === 'confirm' && (
          <Button
            size="sm"
            variant="outline"
            disabled={acceptMutation.isPending}
            onClick={() => acceptMutation.mutate(item.feedback_id)}
          >
            Yes, it's fixed
          </Button>
        )}
        {(action === 'confirm' || action === 'still_broken_only') && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setStillBrokenFor(item);
              setNote('');
              clearStillBrokenImage();
            }}
          >
            No, still broken
          </Button>
        )}
        {action === 'undo' && (
          <Button
            size="sm"
            variant="ghost"
            data-testid="portal-issue-undo"
            disabled={unAcceptMutation.isPending}
            onClick={() => unAcceptMutation.mutate(item.feedback_id)}
          >
            Actually, still broken
          </Button>
        )}
        {showAddNote && (
          <Button
            size="sm"
            variant="ghost"
            data-testid="portal-issue-add-note"
            onClick={() => {
              setAddNoteFor(item);
              setNoteText('');
            }}
          >
            Add a note
          </Button>
        )}
        {showWithdraw && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            data-testid="portal-issue-withdraw"
            disabled={withdrawMutation.isPending}
            onClick={() => withdrawMutation.mutate(item.feedback_id)}
          >
            No longer needed
          </Button>
        )}
        {showReopen && (
          <Button
            size="sm"
            variant="ghost"
            data-testid="portal-issue-reopen"
            disabled={unWithdrawMutation.isPending}
            onClick={() => unWithdrawMutation.mutate(item.feedback_id)}
          >
            Reopen it
          </Button>
        )}
      </div>
    );
  };

  const columns: DataTableColumn<FeedbackItem>[] = [
    {
      // v0.17.2: 180, not 220. The drawer is a 448px Sheet, and status(220) +
      // report(260) + details(220) + the actions column measured 453px of
      // content against a 421px client width -- 32px of hidden overflow, two
      // stacked horizontal scrollbars, and the Answer button's box running
      // 1378 -> 1450.75 against a 1440px viewport, so it rendered as "Answ".
      // Reclaiming 40px here clears the overflow without touching the shared
      // DataTable or the popover's card layout (which never had the problem
      // -- a client sees stacked cards, not this table).
      key: 'status',
      label: 'Status',
      width: 'w-[180px]',
      render: (item) => renderStatusBlock(item),
    },
    {
      key: 'report',
      label: 'Your report',
      wrap: true,
      maxWidth: 'max-w-[260px]',
      render: (item) => renderReportBlock(item, false),
    },
  ];

  if (anyDetails) {
    columns.push({
      key: 'details',
      label: 'Details',
      wrap: true,
      maxWidth: 'max-w-[220px]',
      render: (item) => <IssueDetails item={item} />,
    });
  }

  if (anyActionable || anyClientAction) {
    columns.push({
      key: 'actions',
      label: '',
      align: 'right',
      render: (item) => renderActionsRow(item),
    });
  }

  if (issuesQuery.isError) {
    return (
      <PanelErrorState
        what="your submissions"
        onRetry={() => issuesQuery.refetch()}
        testId="panel-submissions-error"
      />
    );
  }

  if (issuesQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 px-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your submissions…
      </div>
    );
  }

  const emptyStateNode = (
    <span className="text-muted-foreground" data-testid="portal-empty-state">
      No reports yet. When you send one with "Report Feedback" in the
      top bar, it'll show up here and we'll keep you posted.
    </span>
  );

  return (
    <div className="p-3" data-testid="panel-submissions-tab">
      {allReceived && (
        <p
          className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
          data-testid="portal-all-received-reassurance"
        >
          {ALL_RECEIVED_COPY}
        </p>
      )}

      {variant === 'popover' ? (
        // Visual-review finding (2026-07-24, final pass): the 384px client
        // Popover cannot host the full multi-column DataTable — its
        // status-blurb cell inherits whitespace-nowrap and forces a
        // horizontally-scrolling table into a notification-style popover.
        // This condensed, stacked card list carries the SAME per-submission
        // content (status pill, blurb, report text, capped thumbnail row,
        // details, confirm/withdraw/add-note actions) with no table grid.
        orderedItems.length === 0 ? (
          <p className="py-4 text-center text-sm" data-testid="portal-issues-cards">
            {emptyStateNode}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="portal-issues-cards">
            {orderedItems.map((item) => (
              <li
                key={item.feedback_id}
                className="space-y-2 rounded-md border p-3"
                data-testid="portal-submission-card"
              >
                {renderStatusBlock(item)}
                {renderReportBlock(item, true)}
                {hasDetails(item) && <IssueDetails item={item} />}
                {renderActionsRow(item)}
              </li>
            ))}
          </ul>
        )
      ) : (
        <DataTable<FeedbackItem>
          columns={columns}
          rows={orderedItems}
          rowKey={(item) => item.feedback_id}
          containerTestId="portal-issues-table"
          emptyState={emptyStateNode}
        />
      )}

      <Dialog open={stillBrokenFor !== null} onOpenChange={(open) => !open && closeStillBroken()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>What's still broken?</DialogTitle>
            <DialogDescription>
              Tell us what's still wrong. A screenshot helps us see it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="still-broken-note">What's still happening?</Label>
              <Textarea
                id="still-broken-note"
                data-testid="still-broken-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={5}
                placeholder="Describe what's still broken…"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="still-broken-image">Attach a screenshot (optional)</Label>
              <div>
                <label
                  htmlFor="still-broken-image"
                  data-testid="still-broken-image-label"
                  className={cn(
                    'inline-flex h-8 w-fit cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-500',
                  )}
                >
                  <ImagePlus className="h-4 w-4" />
                  {stillBrokenImage ? 'Change image' : 'Choose image'}
                </label>
                <input
                  id="still-broken-image"
                  ref={stillBrokenFileRef}
                  type="file"
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={handleStillBrokenFile}
                  data-testid="still-broken-image-input"
                  className="sr-only"
                />
              </div>
              {stillBrokenImage && (
                <div
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                  data-testid="still-broken-image-selected"
                >
                  <ImagePlus className="h-3.5 w-3.5" />
                  <span className="truncate">{stillBrokenImage.name}</span>
                  <button
                    type="button"
                    onClick={clearStillBrokenImage}
                    aria-label="Remove attached image"
                    data-testid="still-broken-image-remove"
                    className="ml-auto inline-flex items-center rounded p-0.5 hover:bg-muted"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {stillBrokenImageError && (
                <p className="text-xs text-destructive" data-testid="still-broken-image-error">
                  {stillBrokenImageError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeStillBroken}>
              Cancel
            </Button>
            <Button
              disabled={note.trim().length === 0 || stillBrokenMutation.isPending}
              onClick={() =>
                stillBrokenFor &&
                stillBrokenMutation.mutate({
                  feedbackId: stillBrokenFor.feedback_id,
                  note: note.trim(),
                  image: stillBrokenImage,
                })
              }
            >
              {stillBrokenMutation.isPending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addNoteFor !== null} onOpenChange={(open) => !open && setAddNoteFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a note</DialogTitle>
            <DialogDescription>
              Add anything else that would help. It goes straight onto your report and
              won't change its status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="add-note-text">Your note</Label>
            <Textarea
              id="add-note-text"
              data-testid="add-note-text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              placeholder="Add a note…"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddNoteFor(null)}>
              Cancel
            </Button>
            <Button
              data-testid="add-note-submit"
              disabled={noteText.trim().length === 0 || addNoteMutation.isPending}
              onClick={() =>
                addNoteFor &&
                addNoteMutation.mutate({ feedbackId: addNoteFor.feedback_id, note: noteText.trim() })
              }
            >
              {addNoteMutation.isPending ? 'Adding…' : 'Add note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R4 (SPEC-feedback-return-path-2026-08-17): answer a question from us
          -- available regardless of triage state (a question never requires
          the report to be triaged first). */}
      <Dialog open={answerFor !== null} onOpenChange={(open) => !open && setAnswerFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Answer</DialogTitle>
            <DialogDescription>{answerFor?.operator_question}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="answer-question-text">Your answer</Label>
            <Textarea
              id="answer-question-text"
              data-testid="answer-question-text"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={5}
              placeholder="Type your answer…"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnswerFor(null)}>
              Cancel
            </Button>
            <Button
              data-testid="answer-question-submit"
              disabled={answerText.trim().length === 0 || answerMutation.isPending}
              onClick={() =>
                answerFor &&
                answerMutation.mutate({ feedbackId: answerFor.feedback_id, answer: answerText.trim() })
              }
            >
              {answerMutation.isPending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IssueNotes({ item }: { item: FeedbackItem }) {
  const notes = item.notes ?? [];
  if (notes.length === 0) return null;
  return (
    <ul className="space-y-0.5" data-testid="portal-issue-notes">
      {notes.map((n, i) => {
        const rel = relativePast(n.created_at);
        return (
          <li
            key={`${n.created_at}-${i}`}
            className="text-xs text-muted-foreground"
            data-testid="portal-issue-note-echo"
          >
            <span className="font-medium text-foreground">You:</span> {n.text}
            {rel && <span className="opacity-70"> · {rel}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function IssueDetails({ item }: { item: FeedbackItem }) {
  const key = item.claim_ladder.key;
  const build = buildComparison(item);
  const showNote = !!item.resolution_note;
  const needsExplanation = NOTE_REQUIRED_KEYS.has(key) && !showNote;
  const continuity = CONTINUITY_COPY[key];

  return (
    <div className="space-y-1 text-sm text-muted-foreground">
      {continuity && <p data-testid="portal-continuity-copy">{continuity}</p>}
      {showNote && <p data-testid="portal-resolution-note">{item.resolution_note}</p>}
      {needsExplanation && (
        <p data-testid="portal-resolution-note">{LADDER_BLURB[key]}.</p>
      )}
      {build && (key === 'shipped_awaiting' || key === 'verified') && (
        <p data-testid="portal-build-comparison">{build}</p>
      )}
    </div>
  );
}
